import * as React from "react";
import moment from "moment";
import PropTypes from "prop-types";
import { useAuthContext } from "./authContext";
import StorageUtils from "@src/utils/storageUtils";
import AppUtils from "@src/utils/appUtils";
import { SELECTED_BEHAVIOUR } from "@src/constants/constants";
import { useDCContext } from "./dcContext";
import OperatorHooks from "@src/context/hooks/operatorHooks";

const OperatorContext = React.createContext();
OperatorContext.displayName = "OperatorContext";

const useOperatorContext = () => React.useContext(OperatorContext);

const OperatorContentProvider = ({ children, operatorData }) => {
  const { user, setAvatarInitials, impersonatedSessionContext } =
    useAuthContext();
  const { getDynamicReplacement, setProfileForDC, setSelectedBehaviourForDC } =
    useDCContext();
  const [profile, setContextProfile] = React.useState(operatorData); // "profile" is only set via this contex constructor (setProfile cannot be used anywhere else)

  // Keep local profile state in sync with the operatorData prop and update DC when it changes
  React.useEffect(() => {
    if (operatorData) {
      setContextProfile(operatorData);
      setProfileForDC(operatorData); // update DC context only when operatorData changes
    }
  }, [operatorData, setProfileForDC]);
  const [selectedPeriod, setSelectedPeriod] = React.useState(null);
  const [selectedEvent, setSelectedEvent] = React.useState(null);
  const [periodName, setPeriodName] = React.useState(
    operatorData?.customer?.targetPeriodDuration === "MONTHLY"
      ? "Month"
      : "Period",
  );

  const { data: { latestEvents } = {}, isPending: latestEventsPending } =
    OperatorHooks.useOperatorLatestEvents(profile?.operator?.id);
  const latestEvent = latestEvents?.[0] ?? null;

  /** Load a selected behaviour, or pick the first available one */
  const getSelectedBehaviourFromUrlOrStorage = () => {
    // Try to get from URL first, then from localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const selectedBehaviourFromUrl = urlParams.get("selectedBehaviourAcronym");

    let selectedBehaviourFromStorage =
      StorageUtils.getObjectItem(SELECTED_BEHAVIOUR);
    // remove invalid behaviour from storage (e.g. when behaviours have been updated and the previously selected behaviour no longer exists)
    if (
      selectedBehaviourFromStorage &&
      !selectedBehaviourFromStorage.behaviourAcronym
    ) {
      StorageUtils.removeItem(SELECTED_BEHAVIOUR);
      selectedBehaviourFromStorage = null;
    }

    const behaviourAcronym =
      selectedBehaviourFromUrl ||
      selectedBehaviourFromStorage?.behaviourAcronym;

    if (behaviourAcronym) {
      // guard against profile or behaviours being unavailable
      const newBehaviour = profile?.behaviours?.find(
        (b) => b.behaviourAcronym === behaviourAcronym,
      );
      if (newBehaviour) {
        StorageUtils.setObjectItem(SELECTED_BEHAVIOUR, newBehaviour);
        // Remove the param from the URL
        const url = new URL(window.location);
        url.searchParams.delete("selectedBehaviourAcronym");
        window.history.replaceState({}, document.title, url.toString());
        return newBehaviour;
      }
    }
    const fallbackBehaviour = profile?.behaviours?.[0] || null;

    if (fallbackBehaviour) {
      StorageUtils.setObjectItem(SELECTED_BEHAVIOUR, fallbackBehaviour);
    }
    return fallbackBehaviour;
  };

  const [selectedBehaviour, setSelectedNavigationBehaviour] =
    React.useState(null);

  // Ensure DC is only updated when a valid behaviour exists
  React.useEffect(() => {
    if (selectedBehaviour) {
      setSelectedBehaviourForDC(selectedBehaviour);
    }
  }, [selectedBehaviour, setSelectedBehaviourForDC]);

  // Resolve selected behaviour once profile.behaviours or URL/storage data is available.
  React.useEffect(() => {
    // If state already has a selection, do nothing
    if (selectedBehaviour) return;

    const urlParams = new URLSearchParams(window.location.search);
    const hasUrlParam = !!urlParams.get("selectedBehaviourAcronym");
    const stored = StorageUtils.getObjectItem(SELECTED_BEHAVIOUR);

    // If URL or storage indicate a desired behaviour, try to resolve it (getSelectedBehaviourFromUrlOrStorage is safe to call now)
    if (hasUrlParam || stored) {
      const resolved = getSelectedBehaviourFromUrlOrStorage();
      if (resolved) {
        setSelectedNavigationBehaviour(resolved);
        return;
      }
    }

    // If no selection in URL/storage, but profile behaviours are loaded, pick the first behaviour as a fallback
    if (profile?.behaviours?.length) {
      const fallback = profile.behaviours[0];
      setSelectedNavigationBehaviour(fallback);
      StorageUtils.setObjectItem(SELECTED_BEHAVIOUR, fallback);
    }
  }, [profile, selectedBehaviour]);

  const setSelectedBehaviour = (behaviour) => {
    setSelectedNavigationBehaviour(behaviour);
    setSelectedBehaviourForDC(behaviour);
    StorageUtils.setObjectItem(SELECTED_BEHAVIOUR, behaviour);
  };

  //Onboarding flow
  //needs to open on load when operatorMetadata is available but !operatorMetadata.onboardingCommitment and !impersonated
  const [onboardingOpen, setOnboardingOpen] = React.useState(
    !!profile?.operatorMetadata &&
      !profile?.operatorMetadata?.find(
        (meta) => meta.key === "onboarding_commitment",
      ) &&
      !impersonatedSessionContext
      ? "first"
      : null,
  );

  //GDPR
  //needs to show when dataPrivacyAgreement is older than 30 days or !dataPrivacyAgreementSigned
  const dataPrivacyAgreement =
    profile?.appUser?.dataPrivacyAgreement != null
      ? moment.utc(profile.appUser.dataPrivacyAgreement)
      : null;
  const [gdprOpen, setGdprOpen] = React.useState(
    dataPrivacyAgreement == null ||
      dataPrivacyAgreement.isBefore(moment.utc().subtract(30, "days")),
  );

  //role alias
  const roleAlias =
    (!!profile?.operatorMetadata &&
      profile?.operatorMetadata?.find((meta) => meta.key === "role_alias")
        ?.value) ||
    "Captain"; //default to Captain if not set;
  const getFirstName = React.useCallback(() => {
    if (profile?.appUser?.firstname) {
      return profile.appUser.firstname;
    }
    if (user?.firstname) {
      return user.firstname;
    }
    const operatorNameInMetadata = (profile?.operatorMetadata || []).find(
      (meta) => meta.key === "operator_name",
    );
    if (operatorNameInMetadata) {
      return operatorNameInMetadata.value?.split(" ")[0]; // Return the first name from the operator name metadata
    }
    return "No Name";
  }, [profile?.appUser?.firstname, profile?.operatorMetadata, user.firstname]);

  const getLatestVehicle = () => {
    const eventVehicleIdentifier = latestEvent?.metadata.find(
      (m) => m.key === "imo" || m.key === "aircraft_reg_code",
    )?.value;
    const currentVehicleSettings = profile?.customer?.vehicleSettings?.find(
      (vs) =>
        vs.vehicleKey.toUpperCase() === latestEvent?.vehicle.toUpperCase() ||
        vs.vehicleKey === eventVehicleIdentifier, // alternative way to match vehicle
    )?.jsonData;
    return {
      id: eventVehicleIdentifier,
      name: AppUtils.capitalizeFirstLetter(latestEvent?.vehicle),
      settings: currentVehicleSettings,
    };
  };

  const getLastName = React.useCallback(() => {
    if (profile?.appUser?.lastname) {
      return profile.appUser.lastname;
    }
    if (user?.lastname) {
      return user.lastname;
    }
    const operatorLastNameInMetadata = (profile?.operatorMetadata || []).find(
      (meta) => meta.key === "operator_lastname",
    );
    if (operatorLastNameInMetadata?.value) {
      return operatorLastNameInMetadata.value;
    }
    const operatorNameInMetadata = (profile?.operatorMetadata || []).find(
      (meta) => meta.key === "operator_name",
    );
    if (operatorNameInMetadata) {
      // Return the last name from the operator name metadata, if it holds more than one word
      const nameParts = (operatorNameInMetadata.value || "")
        .trim()
        .split(/\s+/);
      return nameParts.length > 1 ? nameParts.at(-1) : "";
    }
    return "No Last Name";
  }, [profile?.appUser?.lastname, profile?.operatorMetadata, user.lastname]);

  setAvatarInitials(`${getFirstName()[0] || "N"}${getLastName()[0] || "N"}`);

  const memoizedProfile = React.useMemo(() => profile, [profile]);

  /** @type {Record<'operatorId'|'key'|'type'|'value'|'modifiedAt', string>} */
  const commitment = React.useMemo(() => {
    return memoizedProfile?.operatorMetadata?.find(
      (i) => i.key === "onboarding_commitment",
    );
  }, [memoizedProfile?.operatorMetadata]);

  const greeting = React.useMemo(() => {
    return getDynamicReplacement(
      !!commitment &&
        moment(commitment.modifiedAt).isAfter(moment().subtract(1, "day"))
        ? "captain__overview__first_day_greeting"
        : "captain__overview__greeting",
      {
        FIRSTNAME: getFirstName(),
        LASTNAME: getLastName(),
        ROLE_ALIAS: AppUtils.formatToTitleCase(roleAlias),
      },
    );
  }, [commitment, roleAlias, getDynamicReplacement, getFirstName, getLastName]);

  const setProfile = (newProfile) => {
    setProfileForDC(newProfile); // also update the profile in DC context so that dynamic replacements depending on profile data are updated across the app
    setContextProfile(newProfile);
  };

  const contextValues = {
    profile: memoizedProfile, // expected to remain stable across app renders
    setProfile, // function to update the profile
    getFirstName,
    getLastName,
    periodName,
    setPeriodName,
    roleAlias,
    selectedPeriod, // if this change all inner components will re-render
    setSelectedPeriod,
    selectedBehaviour, // if this change all inner components will re-render
    setSelectedBehaviour,
    selectedEvent,
    setSelectedEvent,
    latestEvent,
    latestEventsPending,
    getLatestVehicle,
    onboardingOpen,
    setOnboardingOpen,
    gdprOpen,
    setGdprOpen,
    greeting,
    commitment,
  };

  if (!profile?.behaviours?.length || !selectedBehaviour) {
    return null;
  }

  return (
    <OperatorContext.Provider value={contextValues}>
      {children}
    </OperatorContext.Provider>
  );
};

OperatorContentProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { useOperatorContext };

export default OperatorContentProvider;
